import { AnyAction } from 'redux';
import { SagaIterator } from 'redux-saga';
import { call, put, takeLatest } from 'redux-saga/effects';
import { AboFilterI } from 'src/adminPanel/components/pages/office/AboSubscriptionFilter';
import {
    amendAboSubscription,
    retrieveAboSubscriptionOrderProducts,
    retrieveAboTemplateById,
    retrieveAboTemplatesByCustomerId,
    retrieveAboTemplatesByFilter,
    saveAboSubscription,
} from 'src/api/aboTemplates';
import { PageableQueryParams } from 'src/entities/basic-types/Pageable';
import { enqueueSnackbarAction } from '../notifications/actions';
import {
    fetchAboTemplatesByFilter,
    FETCH_ABO_SUBSCRIPTION_ORDER_PRODUCTS,
    FETCH_ABO_TEMPLATES_BY_CUSTOMER_ID,
    FETCH_ABO_TEMPLATES_BY_CUSTOMER_ID_AND_FILTER,
    FETCH_ABO_TEMPLATE_BY_ID,
    INSERT_ABO_SUBSCRIPTION,
    requestFetchAboSubscriptionOrderProducts,
    requestFetchAboSubscriptionOrderProductsFailure,
    requestFetchAboSubscriptionOrderProductsSuccess,
    requestFetchAboTemplateById,
    requestFetchAboTemplateByIdFailure,
    requestFetchAboTemplateByIdSuccess,
    requestFetchAboTemplatesByCustomerId,
    requestFetchAboTemplatesByCustomerIdFailure,
    requestFetchAboTemplatesByCustomerIdSuccess,
    requestFetchAboTemplatesByFilter,
    requestFetchAboTemplatesByFilterFailure,
    requestFetchAboTemplatesByFilterSuccess,
    requestInsertAboSubscription,
    requestInsertAboSubscriptionFailure,
    requestInsertAboSubscriptionSuccess,
    requestUpdateAboSubscription,
    requestUpdateAboSubscriptionFailure,
    requestUpdateAboSubscriptionSuccess,
    UPDATE_ABO_SUBSCRIPTION,
} from './actions';

function* fetchAboTemplatesByCustomerSaga(action: AnyAction): SagaIterator {
    yield put(requestFetchAboTemplatesByCustomerId());

    const { error, response } = yield call(retrieveAboTemplatesByCustomerId, action.customerId, action.pageable);

    if (error) {
        yield put(requestFetchAboTemplatesByCustomerIdFailure(error));
        yield put(
            enqueueSnackbarAction({
                message: error.message,
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        yield put(requestFetchAboTemplatesByCustomerIdSuccess(response));
    }
}

export function* generateFetchAboTemplatesByCustomerIdWatcher() {
    yield takeLatest(FETCH_ABO_TEMPLATES_BY_CUSTOMER_ID, fetchAboTemplatesByCustomerSaga);
}

function* fetchAboTemplatesByFilterSaga(action: AnyAction): SagaIterator {
    yield put(requestFetchAboTemplatesByFilter());

    const { error, response } = yield call(retrieveAboTemplatesByFilter, action.pageable, action.filter);

    if (error) {
        yield put(requestFetchAboTemplatesByFilterFailure(error));
        yield put(
            enqueueSnackbarAction({
                message: error.message,
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        yield put(requestFetchAboTemplatesByFilterSuccess(response));
    }
}

export function* generateFetchAboTemplatesByFilterWatcher() {
    yield takeLatest(FETCH_ABO_TEMPLATES_BY_CUSTOMER_ID_AND_FILTER, fetchAboTemplatesByFilterSaga);
}

function* fetchAboTemplateByIdSaga(action: AnyAction): SagaIterator {
    yield put(requestFetchAboTemplateById());

    const { error, response } = yield call(retrieveAboTemplateById, action.templateId);

    if (error) {
        yield put(requestFetchAboTemplateByIdFailure(error));
        yield put(
            enqueueSnackbarAction({
                message: error.message,
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        yield put(requestFetchAboTemplateByIdSuccess(response));
    }
}

export function* generateFetchAboTemplateByIdWatcher() {
    yield takeLatest(FETCH_ABO_TEMPLATE_BY_ID, fetchAboTemplateByIdSaga);
}

function* fetchAboSubscriptionOrderProductSaga(): SagaIterator {
    yield put(requestFetchAboSubscriptionOrderProducts());

    const { error, response } = yield call(retrieveAboSubscriptionOrderProducts);

    if (error) {
        yield put(requestFetchAboSubscriptionOrderProductsFailure(error));
        yield put(
            enqueueSnackbarAction({
                message: error.message,
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        yield put(requestFetchAboSubscriptionOrderProductsSuccess(response));
    }
}

export function* generateFetchAboSubscriptionOrderProductWatcher() {
    yield takeLatest(FETCH_ABO_SUBSCRIPTION_ORDER_PRODUCTS, fetchAboSubscriptionOrderProductSaga);
}

function* insertAboSubscriptionSaga(action: AnyAction): SagaIterator {
    yield put(requestInsertAboSubscription());

    const { error, response } = yield call(saveAboSubscription, action.aboSubscription);

    if (error) {
        yield put(requestInsertAboSubscriptionFailure(error));
        yield put(
            enqueueSnackbarAction({
                message: error.message,
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        yield put(requestInsertAboSubscriptionSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: 'Abo subscription created successfully',
                options: { key: new Date().getTime() + Math.random(), variant: 'success' },
            })
        );
        yield put(
            fetchAboTemplatesByFilter(
                { page: 0, pageSize: 50 } as PageableQueryParams,
                { customerId: 20000000 } as AboFilterI
            )
        );
    }
}

export function* generateInsertAboSubscriptionWatcher() {
    yield takeLatest(INSERT_ABO_SUBSCRIPTION, insertAboSubscriptionSaga);
}

function* updateAboSubscriptionSaga(action: AnyAction): SagaIterator {
    yield put(requestUpdateAboSubscription());

    const { error, response } = yield call(amendAboSubscription, action.aboSubscription);

    if (error) {
        yield put(requestUpdateAboSubscriptionFailure(error));
        yield put(
            enqueueSnackbarAction({
                message: error.message,
                options: { key: new Date().getTime() + Math.random(), variant: 'error' },
            })
        );
    } else {
        yield put(requestUpdateAboSubscriptionSuccess(response));
        yield put(
            enqueueSnackbarAction({
                message: 'Abo subscription updated successfully',
                options: { key: new Date().getTime() + Math.random(), variant: 'success' },
            })
        );
        yield put(
            fetchAboTemplatesByFilter(
                { page: 0, pageSize: 50 } as PageableQueryParams,
                { customerId: 20000000 } as AboFilterI
            )
        );
    }
}

export function* generateUpdateAboSubscriptionWatcher() {
    yield takeLatest(UPDATE_ABO_SUBSCRIPTION, updateAboSubscriptionSaga);
}
